export type CancelFunc = (reason?: any) => void;
export type Cancelable = { cancel: CancelFunc };
export type CancelablePromise<T> = Promise<T> & Cancelable;
export type Func<T = any, A extends any[] = any[]> = (...args: A) => T;
export type CancelableFunc<T = any, A extends any[] = any[]> = Func<
  CancelablePromise<T>,
  A
>;
export type MaybeCancelableFunc<T = any, A extends any[] = any[]> =
  | CancelableFunc<T, A>
  | Func<T, A>;

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/withResolvers
export const withResolvers = <T>() => {
  let resolve: (value: T | PromiseLike<T>) => void;
  let reject: (reason?: any) => void;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve: resolve!, reject: reject! };
};

export type EventListener<T = any> = {
  addEventListener<K extends keyof T>(
    type: K,
    listener: (this: AbortSignal, ev: T[K]) => any,
    options?: boolean | AddEventListenerOptions,
  ): void;
  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void;
  removeEventListener<K extends keyof T>(
    type: K,
    listener: (this: AbortSignal, ev: T[K]) => any,
    options?: boolean | EventListenerOptions,
  ): void;
  removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ): void;
};

export const once = <T>(
  eventListener: EventListener<T>,
  type: keyof T,
  listener: (/*this: EventListener<T>,*/ ev: T[typeof type]) => any,
  options?: Omit<AddEventListenerOptions, "once">,
) => {
  const cleanup = () => eventListener.removeEventListener(type, listener);
  eventListener.addEventListener(type, listener, { ...options, once: true });
  return cleanup;
};

const isPromise = (p: any): p is Promise<any> => !!(p as Promise<any>)?.then;

export const addAbortSignal = <T>(
  p: Promise<T> | T,
  signal: AbortSignal,
): Promise<T> => {
  const { promise, resolve, reject } = withResolvers<T>();
  if (isPromise(p)) {
    const cleanup = once(signal, "abort", () => reject(signal.reason));
    p.then(resolve).catch(reject).finally(cleanup);
  } else {
    resolve(p);
  }
  return promise;
};

export const addAbortController = <T>(
  p: Promise<T> | T,
  abortController: AbortController,
): CancelablePromise<T> => {
  return Object.assign(addAbortSignal(p, abortController.signal), {
    cancel: abortController.abort.bind(abortController),
  });
};

type ChainAbortSignal = {
  <T extends Cancelable>(p: T, signal: AbortSignal): T;
  <T>(p: T, signal: AbortSignal): T;
};

export const chainAbortSignal = (<T>(_p: T, signal: AbortSignal): T => {
  const p = (
    (_p as any).cancel
      ? (_p as T & Cancelable)
      : (_p as any)?.then
        ? addAbortSignal(_p as any, signal)
        : _p
  ) as T & Cancelable;
  if (!(p as any).cancel) return p;
  once(signal, "abort", () => p?.cancel(signal.reason));
  return p;
}) as ChainAbortSignal;

export const runCancelable = <T>(
  f: (arg: { signal: AbortSignal }) => Promise<T> | T,
  abortController: AbortController = new AbortController(),
) => {
  return addAbortController(
    f({ signal: abortController.signal }),
    abortController,
  );
};

export const chainCancelable = <T>(
  f: Promise<T> | T | ((arg: { signal: AbortSignal }) => Promise<T> | T),
  signal: AbortSignal,
) => {
  return typeof f === "function"
    ? addAbortSignal(
        (f as (arg: { signal: AbortSignal }) => Promise<T> | T)({ signal }),
        signal,
      )
    : addAbortSignal(f, signal);
};
